import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  InputAdornment,
  TableSortLabel,
  Tooltip,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Favorite as WishlistIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";

// ✅ Import named exports from product service
import { getProducts, deleteProduct, searchProducts } from '../services/productService';
import { useDispatch, useSelector } from 'react-redux';
import { addToWishlistAsync } from '../../wishlist/store/wishlistSlice';

const ProductsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading: wishlistLoading, error: wishlistError } = useSelector(state => state.wishlist || {});
  
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // ✅ Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // ✅ Sorting state
  const [orderBy, setOrderBy] = useState('Name');
  const [order, setOrder] = useState('asc');
  
  // ✅ Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // ✅ View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ✅ Wishlist dialog state
  const [wishlistDialog, setWishlistDialog] = useState({
    open: false,
    product: null,
    quantity: 1,
    notes: '',
    loading: false
  });

  // ✅ Filter type state
  const [filterType, setFilterType] = useState('all');

  // ✅ Sorting function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting
  };

  // ✅ Sort data function
  const sortData = (data, orderBy, order) => {
    return data.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // ✅ Handle different data types
      if (orderBy === 'UnitPrice' || orderBy === 'CostPrice' || orderBy === 'StockQuantity') {
        // Numeric sorting
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (orderBy === 'IsActive') {
        // Boolean sorting (Active first when asc)
        aValue = a[orderBy] ? 1 : 0;
        bValue = b[orderBy] ? 1 : 0;
      } else {
        // String sorting (case-insensitive)
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }
      
      if (order === 'desc') {
        return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
      }
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    });
  };

  // ✅ Fetch products with proper payload handling
  const fetchProducts = async () => {
    console.log('📦 Fetching products using productService...');
    setLoading(true);
    try {
      const response = await getProducts();
      
      // ✅ Handle different response structures
      let productData = [];
      if (Array.isArray(response)) {
        productData = response;
      } else if (response && response.payload && Array.isArray(response.payload)) {
        productData = response.payload;
      } else if (response && Array.isArray(response.data)) {
        productData = response.data;
      } else {
        console.warn('⚠️ Unexpected response structure:', response);
        productData = [];
      }
      
      setAllProducts(productData);
      setFilteredProducts(productData);
      console.log('✅ Products loaded:', productData.length);
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      toast.error('Failed to load products');
      setAllProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Search detection: integer = SKU, alphabets = name
  // ✅ Update detectSearchType to include barcode
  const detectSearchType = (searchValue) => {
    const trimmedValue = searchValue.trim();

    // Barcodes are usually numeric and can be long, but let's check for a typical barcode length (8-14 digits)
    const isBarcode = /^\d{8,14}$/.test(trimmedValue);
    const isInteger = /^\d+$/.test(trimmedValue);

    if (isBarcode) {
      return 'barcode';
    } else if (isInteger) {
      return 'sku';
    } else {
      return 'name';
    }
  };

  // ✅ Search products with simple detection
  const handleSearch = async (searchValue = searchTerm) => {
    if (!searchValue.trim()) {
      // If search is empty, show all products
      setFilteredProducts(allProducts);
      setPage(0);
      return;
    }

    setIsSearching(true);

    try {
      const trimmedValue = searchValue.trim();
      let paramsObj = {};

      // Detect search type and build paramsObj
      if (/^\d{8,14}$/.test(trimmedValue)) {
        // Barcode (8-14 digits)
        paramsObj = { barCode: trimmedValue };
      } else if (/^\d+$/.test(trimmedValue)) {
        // SKU (all digits, but not barcode length)
        paramsObj = { sku: trimmedValue };
      } else {
        // Name
        paramsObj = { name: trimmedValue };
      }

      const response = await searchProducts(paramsObj);

      let searchData = [];
      if (Array.isArray(response)) {
        searchData = response;
      } else if (response && response.payload && Array.isArray(response.payload)) {
        searchData = response.payload;
      } else if (response && Array.isArray(response.data)) {
        searchData = response.data;
      } else {
        searchData = [];
      }

      setFilteredProducts(searchData);
      setPage(0);

      if (searchData.length === 0) {
        toast.info(`No products found for "${searchValue}"`);
      }
    } catch (error) {
      toast.error('Failed to search products');
      setFilteredProducts(allProducts);
    } finally {
      setIsSearching(false);
    }
  };

  // ✅ Handle search input change with debounce
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchTerm !== '') {
        handleSearch(searchTerm);
      } else {
        setFilteredProducts(allProducts); // Show all products when search is cleared
      }
    }, 500); // 500ms delay for debouncing

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, allProducts]);

  // ✅ Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredProducts(allProducts);
    setPage(0);
  };

  // ✅ Manual search trigger (for Enter key or button click)
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchTerm.trim()) {
      handleSearch(searchTerm);
    }
  };

  // ✅ Load products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // ✅ Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ✅ View action handler
  const handleView = (product, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log('👁️ VIEW CLICKED - Product:', product.Name);
    // ✅ Now call handleViewProduct instead of opening dialog
    handleViewProduct(product.Id);
  };

  // ✅ Close view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedProduct(null);
  };

  // ✅ Edit Handler - Navigation Only
  const handleEdit = (product, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log('📝 EDIT CLICKED - Product:', product.Name);
    navigate(`/products/edit/${product.Id}`, {
      state: { 
        productData: product,
        timestamp: Date.now()
      }
    });
  };

  // ✅ Update handleEditProduct to pass product data
  const handleEditProduct = (productId) => {
    // ✅ Find the product object by ID
    const productToEdit = allProducts.find(product => product.Id === productId);
    
    if (productToEdit) {
      console.log('📝 Navigating to edit with product data:', productToEdit);
      
      // ✅ Navigate with product data in state
      navigate(`/products/edit/${productId}`, {
        state: { 
          productData: productToEdit  // ✅ Pass the entire product object
        }
      });
    } else {
      console.error('❌ Product not found for editing:', productId);
      toast.error('Product not found');
    }
  };

  // ✅ Add new product handler
  const handleAddNew = () => {
    console.log('➕ Adding new product');
    navigate('/products/add');
  };

  // ✅ Delete handler
  const handleDelete = async (productId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        console.log('🗑️ Deleting product:', productId);
        await deleteProduct(productId);
        toast.success('Product deleted successfully');
        
        // ✅ Remove from both arrays
        const updatedAllProducts = allProducts.filter(p => p.Id !== productId);
        const updatedFilteredProducts = filteredProducts.filter(p => p.Id !== productId);
        
        setAllProducts(updatedAllProducts);
        setFilteredProducts(updatedFilteredProducts);
        
        // ✅ Reset page if current page would be empty after deletion
        const newProductCount = updatedFilteredProducts.length;
        const maxPage = Math.ceil(newProductCount / rowsPerPage) - 1;
        if (page > maxPage && maxPage >= 0) {
          setPage(maxPage);
        }
        
      } catch (error) {
        console.error('❌ Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  // ✅ Refresh handler
  const handleRefresh = () => {
    console.log('🔄 Refreshing products list');
    setPage(0);
    setSearchTerm('');
    fetchProducts();
  };

  // ✅ Add to wishlist handler
  const handleAddToWishlist = (product) => {
    setWishlistDialog({
      open: true,
      product: product,
      quantity: 1,
      notes: '',
      loading: false
    });
  };

  // ✅ Wishlist input change handler
  const handleWishlistInputChange = (field, value) => {
    setWishlistDialog(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ✅ Wishlist submit handler
  const handleWishlistSubmit = async () => {
    try {
      setWishlistDialog(prev => ({ ...prev, loading: true }));
      
      const wishlistData = {
        productId: wishlistDialog.product.Id,
        productName: wishlistDialog.product.ProductName,
        price: wishlistDialog.product.Price,
        quantity: wishlistDialog.quantity,
        notes: wishlistDialog.notes,
        totalAmount: wishlistDialog.product.Price * wishlistDialog.quantity,
        category: wishlistDialog.product.Category,
        sku: wishlistDialog.product.SKU,
        stockQuantity: wishlistDialog.product.StockQuantity
      };

      const result = await dispatch(addToWishlistAsync(wishlistData));
      
      if (addToWishlistAsync.fulfilled.match(result)) {
        toast.success(`${wishlistDialog.product.ProductName} added to wishlist!`);
        handleWishlistClose();
      } else {
        throw new Error(result.payload || 'Failed to add to wishlist');
      }
      
    } catch (error) {
      console.error('❌ Error adding to wishlist:', error);
      toast.error('Failed to add product to wishlist');
    } finally {
      setWishlistDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // ✅ Wishlist close handler
  const handleWishlistClose = () => {
    setWishlistDialog({
      open: false,
      product: null,
      quantity: 1,
      notes: '',
      loading: false
    });
  };

  // ✅ Apply sorting to filtered products
  const safeFilteredProducts = Array.isArray(filteredProducts) ? filteredProducts : [];
  const sortedProducts = sortData([...safeFilteredProducts], orderBy, order);
  const paginatedProducts = sortedProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ✅ Get search type for display
  const getSearchTypeDisplay = () => {
    if (!searchTerm.trim()) return '';
    const type = detectSearchType(searchTerm);
    if (type === 'barcode') return '(searching by BarCode)';
    if (type === 'sku') return '(searching by SKU)';
    return '(searching by name)';
  };

  // ✅ Update handleViewProduct to use filteredProducts instead of products
  const handleViewProduct = (productId) => {
    const productToView = filteredProducts.find(product => product.Id === productId);
    
    if (productToView) {
      console.log('👁️ Navigating to product detail page:', productToView.Name);
      navigate(`/products/detail/${productId}`, {
        state: { 
          productData: productToView
        }
      });
    } else {
      toast.error('Product not found');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* ✅ Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          {t("product_list")}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={loading || isSearching}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ 
              minWidth: 140,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            {t("add_product")}
          </Button>
        </Box>
      </Box>

      {/* ✅ Search Bar */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t("search_products_placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t("filter")}</InputLabel>
              <Select
                value={filterType}
                label={t("filter")}
                onChange={(e) => setFilterType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">{t("all_products")}</MenuItem>
                <MenuItem value="active">{t("active_only")}</MenuItem>
                <MenuItem value="inactive">{t("inactive_only")}</MenuItem>
                <MenuItem value="low_stock">{t("low_stock")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Chip
                label={`${safeFilteredProducts.length} ${t("products")}`}
                color="primary"
                variant="outlined"
                icon={<FilterListIcon />}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Search Status */}
        {searchTerm && !isSearching && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("search_hint")}: {getSearchTypeDisplay()}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ✅ Products Table with Sortable Headers */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            py: 8 
          }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {/* ✅ Sortable Product Name */}
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'Name'}
                        direction={orderBy === 'Name' ? order : 'asc'}
                        onClick={() => handleSort('Name')}
                        sx={{ 
                          '&.MuiTableSortLabel-root': {
                            color: 'inherit',
                            fontWeight: 600
                          }
                        }}
                      >
                        {t("product_name")}
                      </TableSortLabel>
                    </TableCell>

                    {/* ✅ Sortable SKU */}
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'SKU'}
                        direction={orderBy === 'SKU' ? order : 'asc'}
                        onClick={() => handleSort('SKU')}
                        sx={{ 
                          '&.MuiTableSortLabel-root': {
                            color: 'inherit',
                            fontWeight: 600
                          }
                        }}
                      >
                        SKU
                      </TableSortLabel>
                    </TableCell>

                    {/* ✅ Sortable Category */}
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'Category'}
                        direction={orderBy === 'Category' ? order : 'asc'}
                        onClick={() => handleSort('Category')}
                        sx={{ 
                          '&.MuiTableSortLabel-root': {
                            color: 'inherit',
                            fontWeight: 600
                          }
                        }}
                      >
                        {t("category")}
                      </TableSortLabel>
                    </TableCell>

                    {/* ✅ Sortable Unit Price */}
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'UnitPrice'}
                        direction={orderBy === 'UnitPrice' ? order : 'asc'}
                        onClick={() => handleSort('UnitPrice')}
                        sx={{ 
                          '&.MuiTableSortLabel-root': {
                            color: 'inherit',
                            fontWeight: 600
                          }
                        }}
                      >
                        {t("unit_price")}
                      </TableSortLabel>
                    </TableCell>

                    {/* ✅ Sortable Stock Quantity */}
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'StockQuantity'}
                        direction={orderBy === 'StockQuantity' ? order : 'asc'}
                        onClick={() => handleSort('StockQuantity')}
                        sx={{ 
                          '&.MuiTableSortLabel-root': {
                            color: 'inherit',
                            fontWeight: 600
                          }
                        }}
                      >
                        {t("stock")}
                      </TableSortLabel>
                    </TableCell>

                    {/* ✅ Sortable Status */}
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'IsActive'}
                        direction={orderBy === 'IsActive' ? order : 'asc'}
                        onClick={() => handleSort('IsActive')}
                        sx={{ 
                          '&.MuiTableSortLabel-root': {
                            color: 'inherit',
                            fontWeight: 600
                          }
                        }}
                      >
                        {t("status")}
                      </TableSortLabel>
                    </TableCell>

                    {/* ✅ Non-sortable Actions */}
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      {t("actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProducts.map((product) => (
                    <TableRow key={product.Id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {product.Name}
                          </Typography>
                          {product.Description && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {product.Description.length > 50 
                                ? `${product.Description.substring(0, 50)}...` 
                                : product.Description
                              }
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {product.SKU}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {/* ✅ Handle different property names from backend */}
                          {product.CategoryName || 
                           product.Category?.CategoryName || 
                           product.ProductCategory?.CategoryName || 
                           product.category?.name ? (
                            <Chip
                              label={
                                product.CategoryName || 
                                product.Category?.CategoryName || 
                                product.ProductCategory?.CategoryName || 
                                product.category?.name
                              }
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ borderRadius: 1 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              {t("no_category")}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          €{product.SalePrice?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: (product.StockQuanity || product.StockQuantity || product.stockQuantity || 0) <= (product.MinStockLevel || product.minStockLevel || 0) 
                                ? 'error.main' 
                                : 'text.primary'
                            }}
                          >
                            {/* ✅ Handle different property names from backend */}
                            {product.StockQuanity || product.StockQuantity || product.stockQuantity || 0}
                          </Typography>
                          
                          {/* ✅ Show minimum stock level if available */}
                          {(product.MinStockLevel || product.minStockLevel) && (
                            <Typography variant="caption" color="text.secondary">
                              Min: {product.MinStockLevel || product.minStockLevel}
                            </Typography>
                          )}
                          
                          {/* ✅ Low stock warning */}
                          {(product.StockQuanity || product.StockQuantity || product.stockQuantity || 0) <= 
                           (product.MinStockLevel || product.minStockLevel || 0) && 
                           (product.MinStockLevel || product.minStockLevel) > 0 && (
                            <Chip
                              label={t("low_stock")}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={product.IsActive ? t("active") : t("inactive")} 
                          size="small" 
                          color={product.IsActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {/* ✅ ADD THIS WISHLIST ICON BEFORE YOUR EXISTING ICONS */}
                          <Tooltip title={t("add_to_wishlist")}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleAddToWishlist(product)}
                            >
                              <WishlistIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <IconButton
                            size="small"
                            color="info"
                            onClick={(event) => handleView(product, event)}
                            title={t("view_product_details")}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(event) => handleEdit(product, event)}
                            title={t("edit_product")}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(event) => handleDelete(product.Id, event)}
                            title={t("delete_product")}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={safeFilteredProducts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            />
          </>
        )}

        {/* ✅ Empty State - No Products in System */}
        {!loading && !isSearching && safeFilteredProducts.length === 0 && !searchTerm && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            py: 8,
            gap: 2
          }}>
            <Typography variant="h6" color="text.secondary">
              {t("no_products_found")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("get_started_by_adding_your_first_product")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{ textTransform: 'none' }}
            >
              {t("add_first_product")}
            </Button>
          </Box>
        )}

        {/* ✅ Empty State - No Search Results */}
        {!loading && !isSearching && safeFilteredProducts.length === 0 && searchTerm && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            py: 8,
            gap: 2
          }}>
            <Typography variant="h6" color="text.secondary">
              {t("no_products_found_for", { searchTerm })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("searched")}: {getSearchTypeDisplay().replace('(', '').replace(')', '')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("try_a_different_search_term_or_clear_the_search")}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              sx={{ textTransform: 'none' }}
            >
              {t("clear_search")}
            </Button>
          </Box>
        )}
      </Paper>

      {/* ✅ View Product Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("product_details")}</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">{t("product_name")}</Typography>
                <Typography variant="body1">{selectedProduct.Name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">{t("sku")}</Typography>
                <Typography variant="body1">{selectedProduct.SKU}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>{t("close")}</Button>
          <Button onClick={() => { handleCloseViewDialog(); handleEdit(selectedProduct); }} variant="contained">
            {t("edit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Add to Wishlist Dialog */}
      <Dialog 
        open={wishlistDialog.open} 
        onClose={handleWishlistClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WishlistIcon color="secondary" />
              <Typography variant="h6">{t("add_to_wishlist")}</Typography>
            </Box>
            <IconButton onClick={handleWishlistClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 3 }}>
          {wishlistDialog.product && (
            <>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {wishlistDialog.product.ProductName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  SKU: {wishlistDialog.product.SKU || 'N/A'} • {t("category")}: {wishlistDialog.product.Category || 'N/A'}
                </Typography>
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                  €{wishlistDialog.product.Price?.toLocaleString() || '0'}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label={t("quantity")}
                type="number"
                value={wishlistDialog.quantity}
                onChange={(e) => handleWishlistInputChange('quantity', Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1, max: wishlistDialog.product.StockQuantity }}
                helperText={`${t("available_stock")}: ${wishlistDialog.product.StockQuantity}`}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label={t("notes_optional")}
                multiline
                rows={3}
                value={wishlistDialog.notes}
                onChange={(e) => handleWishlistInputChange('notes', e.target.value)}
                placeholder={t("add_any_notes_about_this_wishlist_item")}
                sx={{ mb: 2 }}
              />

              <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="body2" color="text.secondary">
                  {t("total_amount")}
                </Typography>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                  €{((wishlistDialog.product.Price || 0) * wishlistDialog.quantity).toLocaleString()}
                </Typography>
              </Box>

              {wishlistError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {wishlistError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleWishlistClose}
            disabled={wishlistDialog.loading || wishlistLoading}
          >
            {t("cancel")}
          </Button>
          <Button 
            variant="contained"
            onClick={handleWishlistSubmit}
            disabled={wishlistDialog.loading || wishlistLoading}
            startIcon={(wishlistDialog.loading || wishlistLoading) ? <CircularProgress size={20} /> : <WishlistIcon />}
          >
            {(wishlistDialog.loading || wishlistLoading) ? t("adding") : t("add_to_wishlist")}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default ProductsListPage;